import { FileDownloadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback } from "react";
import type { Dimension } from "../../../domain/models/metrics";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { TimeFilter } from "../InternalUsageTab/components/TimeFilter";
import { EARLIEST_DATA_DATE } from "../InternalUsageTab/constants";
import {
  exportSeatHistoryCsv,
  makeSeatHistoryExportFilename,
} from "./exportSeatHistory";
import {
  exportSessionHistoryCsv,
  makeSessionExportFilename,
} from "./exportSessionHistory";
import {
  exportUserUsageCsv,
  makeUserUsageExportFilename,
} from "./exportUserUsage";
import {
  SeatHistoryEntry,
  ShowingOption,
  UserSessionEntry,
  UserUsageEntry,
} from "./types";

interface SessionHistoryToolbarProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  dimension: Dimension;
  onDimensionChange: (dimension: Dimension) => void;
  showingOption: ShowingOption;
  onShowingOptionChange: (option: ShowingOption) => void;
  data: UserSessionEntry[];
  seatHistoryData: SeatHistoryEntry[];
  userUsageData: UserUsageEntry[];
  userDisplayNames?: Map<string, string>;
  userEmails?: Map<string, string>;
  /** Whether session or seat data is currently loading */
  isLoading?: boolean;
}

export const SessionHistoryToolbar: React.FC<SessionHistoryToolbarProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  dimension,
  onDimensionChange,
  showingOption,
  onShowingOptionChange,
  data,
  seatHistoryData,
  userUsageData,
  userDisplayNames,
  userEmails,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useIsMobile();

  const handleExport = useCallback(() => {
    switch (showingOption) {
      case ShowingOption.SEAT_HISTORY: {
        const columnHeaders = {
          grantedViaLicense: t(
            "pages.dataAnalytics.userLevelData.seatHistory.columns.grantedViaLicense",
          ),
          anonymizedUser: t(
            "pages.dataAnalytics.userLevelData.seatHistory.columns.anonymizedUser",
          ),
          dateAssigned: t(
            "pages.dataAnalytics.userLevelData.seatHistory.columns.dateAssigned",
          ),
          currentExpiryDate: t(
            "pages.dataAnalytics.userLevelData.seatHistory.columns.currentExpiryDate",
          ),
          originalExpiryDate: t(
            "pages.dataAnalytics.userLevelData.seatHistory.columns.originalExpiryDate",
          ),
        };
        const filename = makeSeatHistoryExportFilename(startDate, endDate);
        exportSeatHistoryCsv(seatHistoryData, columnHeaders, filename);
        break;
      }

      case ShowingOption.USER_USAGE: {
        const columnHeaders = {
          user: t("pages.dataAnalytics.userLevelData.userUsage.columns.user"),
          email: t("pages.dataAnalytics.userLevelData.userUsage.columns.email"),
          sessions: t(
            "pages.dataAnalytics.userLevelData.userUsage.columns.sessions",
          ),
          uniqueExperiences: t(
            "pages.dataAnalytics.userLevelData.userUsage.columns.uniqueExperiences",
          ),
          duration: t(
            "pages.dataAnalytics.userLevelData.userUsage.columns.duration",
          ),
        };
        const filename = makeUserUsageExportFilename(startDate, endDate);
        exportUserUsageCsv(
          userUsageData,
          columnHeaders,
          filename,
          userDisplayNames,
          userEmails,
        );
        break;
      }

      case ShowingOption.USER_SESSIONS: {
        const columnHeaders = {
          date: t("pages.dataAnalytics.userLevelData.columns.date"),
          userId: t("pages.dataAnalytics.userLevelData.columns.user"),
          experience: t("pages.dataAnalytics.userLevelData.columns.experience"),
          duration: t("pages.dataAnalytics.userLevelData.columns.duration"),
          country: t("pages.dataAnalytics.userLevelData.columns.country"),
          multiplayerSessionId: t(
            "pages.dataAnalytics.userLevelData.columns.multiplayerSessionId",
          ),
        };
        const filename = makeSessionExportFilename(startDate, endDate);
        exportSessionHistoryCsv(
          data,
          columnHeaders,
          filename,
          userDisplayNames,
        );
        break;
      }
    }
  }, [
    data,
    seatHistoryData,
    userUsageData,
    userDisplayNames,
    userEmails,
    showingOption,
    startDate,
    endDate,
    t,
  ]);

  const isExportDisabled = isLoading;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* Date range display + calendar picker */}
      <TimeFilter
        dimension={dimension}
        earliestSelectableDate={EARLIEST_DATA_DATE}
        endDate={endDate}
        onDimensionChange={onDimensionChange}
        setEndDate={onEndDateChange}
        setStartDate={onStartDateChange}
        startDate={startDate}
      />

      {/* Right side: Showing dropdown + Export */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
        {/* Showing dropdown */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography sx={{ fontSize: 12, color: theme.colors.text[1] }}>
            {t("pages.dataAnalytics.userLevelData.showing")}
          </Typography>
          <Select
            onChange={(e) =>
              onShowingOptionChange(e.target.value as ShowingOption)
            }
            size="small"
            sx={{
              minWidth: 180,
              backgroundColor: theme.colors.grey,
              borderRadius: theme.shape.borderRadius * 2,
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
            value={showingOption}
          >
            <MenuItem value={ShowingOption.USER_SESSIONS}>
              {t(
                "pages.dataAnalytics.userLevelData.showingOptions.userSessions",
              )}
            </MenuItem>
            <MenuItem value={ShowingOption.USER_USAGE}>
              {t("pages.dataAnalytics.userLevelData.showingOptions.userUsage")}
            </MenuItem>
            <MenuItem value={ShowingOption.SEAT_HISTORY}>
              {t(
                "pages.dataAnalytics.userLevelData.showingOptions.seatHistory",
              )}
            </MenuItem>
          </Select>
        </Box>

        {/* Export button — hidden on mobile */}
        {!isMobile && (
          <Button
            disableElevation
            disabled={isExportDisabled}
            onClick={handleExport}
            startIcon={<FileDownloadOutlined />}
            sx={{
              backgroundColor: theme.colors.grey,
              border: "none",
              borderRadius: `${theme.shape.borderRadius}px`,
              color: theme.colors.primary[5],
              fontSize: 14,
              fontWeight: 600,
              height: 40,
              letterSpacing: "0.02em",
              minWidth: 129,
              padding: 1,
              gap: 1,
              textTransform: "none",
              "&:hover": {
                backgroundColor: theme.colors.secondary[1],
              },
              "&:disabled": {
                backgroundColor: theme.colors.grey,
                color: theme.colors.secondary[4],
              },
            }}
            variant="contained"
          >
            {t("pages.dataAnalytics.userLevelData.export")}
          </Button>
        )}
      </Box>
    </Box>
  );
};
